import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Lottie from 'lottie-react';

import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Icons,
    Theme,
    Typography,
} from '@thirdspacelearning/library';
import { useEventTracking } from '@tsl-frontend/tsl-utilities';

import { currClassroomURLSelector, nextLessonSelector } from '@store/session';
import { DATADOG_EVENT_NAMES } from '@consts';

import { sessionStartingSoon } from '@assets/app';
import { useIntroLoCompleted } from '@hooks';

const sx = {
    title: {
        display: 'flex',
        fontWeight: 'bold',
    },
    dialog: (theme: Theme) => ({
        '& .MuiDialog-paper': {
            overflow: 'visible',
            padding: theme.spacing(4),
        },
    }),
    content: (theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        padding: theme.spacing(2, 0, 0, 0),
        overflow: 'visible',
    }),
    container: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
    },
    button: (theme: Theme) => ({
        position: 'absolute',
        top: -10,
        right: -10,
        borderRadius: '40px',
        minWidth: 0,
        padding: 1,
        boxShadow: theme.shadows[2],
        color: theme.palette.grey[600],
        background: theme.palette.background.default,
        zIndex: 1,
    }),
    decoration: {
        display: { xs: 'none', sm: 'flex' },
        alignItems: 'center',
        maxWidth: '220px',
    },
};

interface Props {
    showStartSession: boolean;
    sessionDialogOpen: boolean;
    setSessionDialogOpen: (open: boolean) => void;
}

const SessionDialog: FC<Props> = ({
    showStartSession,
    sessionDialogOpen,
    setSessionDialogOpen,
}) => {
    const dispatch = useDispatch();

    const { sendUserTracking } = useEventTracking(dispatch);

    const classroomURL = useSelector(currClassroomURLSelector);
    const nextLesson = useSelector(nextLessonSelector);

    const introLoCompleted = useIntroLoCompleted();

    const loTitle = nextLesson?.title;
    const hasClassroom = Boolean(classroomURL);

    const redirectToClassroom = () => {
        if (hasClassroom) {
            sendUserTracking(DATADOG_EVENT_NAMES.START_SESSION, {
                eventVersion: '1.0.0',
            });
            window.location.href = classroomURL;
        }
    };

    const onClose = () => {
        setSessionDialogOpen(false);
    };

    useEffect(() => {
        setSessionDialogOpen(showStartSession);
    }, [showStartSession, setSessionDialogOpen]);

    return (
        <Dialog
            open={sessionDialogOpen}
            fullWidth={true}
            maxWidth="sm"
            aria-labelledby="session-start-dialog"
            sx={sx.dialog}
            data-testid="startSessionDialog"
        >
            <Box display="flex">
                <Box sx={sx.container}>
                    <Typography variant="h5" sx={sx.title}>
                        Your session with Skye is starting!
                    </Typography>
                    <DialogContent sx={sx.content}>
                        {!introLoCompleted ? (
                            <>
                                <Typography
                                    fontWeight="bold"
                                    data-testid="ai-tutor-intro-title"
                                >
                                    What you'll learn today:
                                </Typography>
                                <Typography
                                    variant="body1"
                                    data-testid="ai-tutor-intro-label"
                                >
                                    AI Tutor Introduction
                                </Typography>
                            </>
                        ) : (
                            loTitle && (
                                <>
                                    <Typography fontWeight="bold">
                                        What you'll learn today:
                                    </Typography>
                                    <Typography variant="body1">
                                        {loTitle}
                                    </Typography>
                                </>
                            )
                        )}

                        <Box mt={2}>
                            <Button
                                color="primary"
                                variant="contained"
                                size="medium"
                                children="Start session"
                                disabled={!hasClassroom}
                                data-testid="start-session-btn"
                                onClick={redirectToClassroom}
                            />
                        </Box>
                    </DialogContent>
                </Box>
                <Box sx={sx.decoration}>
                    <Lottie animationData={sessionStartingSoon} loop={true} />
                </Box>
            </Box>

            <Button
                variant="contained"
                color="inherit"
                onClick={onClose}
                sx={sx.button}
                data-testid="startSessionCloseButton"
            >
                <Icons.Close fontSize="large" />
            </Button>
        </Dialog>
    );
};

export default SessionDialog;
